/* A report, turned into the rows and sentences the result screen renders.
 *
 * Nothing here decides what a page came to. `lib/outcome.ts` owns that, the
 * service owns it before that, and a second copy of the rule in a screen
 * directory is the defect this codebase has already deleted once. What this
 * file does is smaller and is genuinely the screen's: which chip a question
 * wears, which order the questions go in, and whether anything on the page
 * repeats.
 *
 * Three rules are worth stating because each of them is a claim about a child.
 *
 * **A wrong answer with one named cause and a wrong answer with several are
 * not the same claim.** The service says which by status: `identified` is one
 * mechanism it will stand behind, `ambiguous` is more than one slip that would
 * produce the same answer. Printing "we know why" over an ambiguous row would
 * pick one and be confidently wrong about a child, so only `identified` earns
 * the cross with a named cause and everything else wrong earns the dash.
 *
 * **A right answer stays a right answer even when a mechanism is attached.**
 * The recorded page has four answers marked correct that also carry
 * "rounded to a whole number instead": the answer landed on the same number
 * anyway. `is_correct` is the field that decides right from wrong here, and a
 * mechanism list never overrides it.
 *
 * **Nothing claims a repeat unless something repeated.** The recorded tier A
 * page has three wrong answers and three different causes, so the section that
 * would say "the mistake that repeats" has nothing to repeat and says something
 * else instead. `pattern()` returns which of the three cases it found and the
 * screen writes a different sentence for each.
 */
import type {
  Finding, Link, Printable, Report, Topic, Unmarkable, Worked,
} from "./types";
/** What a question's chip says. Defined here and not in the component, because
 *  it is a fact read off a finding rather than a drawing decision, and a lib
 *  module may not depend on a `.tsx` file: the node tsconfig that compiles the
 *  specs has no `jsx`, so a type-only import of one breaks the build. */
export type ChipState = "right" | "cause" | "no-cause" | "unread";

/** The four question states, in one set of words.
 *
 *  **One state, one phrase, wherever it appears.** The walk of 4 September
 *  found question U wearing three of them on one screen: a chip reading
 *  "wrong, cause not settled", a legend entry reading "wrong, cause not
 *  named", and a heading reading "Wrong, cause not certain". A parent matching
 *  the chip to the legend found no entry for the words on the chip. P1-08,
 *  R2-02.
 *
 *  So this is the vocabulary, and the chip, the legend and the heading over
 *  each row all read it. The heading is this phrase with its first letter up
 *  and nothing else changed, so the three cannot drift apart again.
 *
 *  "cause not settled" and not "cause not named", because the dash covers two
 *  kinds of wrong answer: one the read could not explain at all, and one it
 *  can explain several ways and will not pick between. Both are true to "we
 *  have not settled on one", and the row below says which it was. */
export const QUESTION_STATE: Record<ChipState, string> = {
  right: "right",
  cause: "wrong, cause named",
  "no-cause": "wrong, cause not settled",
  unread: "not read",
};

/** One question, as a parent meets it: a chip in the grid and a row below. */
export interface Row {
  /** Stable across renders and unique across topics. */
  key: string;
  /** The question's label on the paper, for example "P". Never a grade. */
  label: string;
  chip: ChipState;
  right: boolean;
  /** Whether the service read this question at all. */
  read: boolean;
  /** The line above the evidence, for example "Wrong, cause named". The chip's
   *  own phrase, capitalised, so the two agree word for word. */
  verdict: string;
  /** Exactly what was written. Null where the service sent no string, and
   *  then no evidence block is drawn: the numeric `answer` is for arithmetic
   *  and showing it would print a float a parent cannot match to the paper. */
  wrote: string | null;
  answer: string | null;
  mechanisms: string[];
  evidence: string[];
  /** The lesson this question sat under, for a page that spans several. */
  topic: string | null;
  /** The library's route to the answer, where the library has a steps writer.
   *  Null on a right row with no writer, on an unmarkable row, and wherever
   *  the service left it absent. The screen offers "How to get there" only
   *  where this is set. */
  worked: Worked | null;
  /** Whether our reader marked this question rather than a library.
   *
   *  Read off `verified`, which the service sets false on exactly that kind of
   *  finding and on nothing else. A finding from before either field existed
   *  is a library finding, because the model path did not exist to make it, so
   *  the absence reads as verified rather than as unknown. The row wears a
   *  small label where this is true, and never a route to the answer: a
   *  library that could write one is the thing this row is waiting for. */
  modelMarked: boolean;
  /** Whether this question was marked from the corpus rather than by a library
   *  or by the reader on the spot.
   *
   *  Read off `source`, and not off `verified`, which is the opposite of the
   *  rule above. `verified === false` is the service saying a mark has nothing
   *  behind it; a corpus mark has something behind it, so the field that
   *  separates it from every other kind is the one naming where it came from.
   *  The row wears a small label where this is true, in its own words. */
  corpusMarked: boolean;
  provisional?: boolean;
  corpusVerified?: boolean;
  verificationNotice?: string;
  /** The answer form this question was checked under, as the service names it,
   *  or null. `formLine` turns it into the sentence a parent reads. */
  form: string | null;
  /** What the form's comparison found, or null where none ran. */
  formCheck: string | null;
  formAccuracy?: { kind: "dp" | "sf"; n: number } | null;
  formRequirements?: { simplest?: boolean } | null;
  /** Which syllabus statement asked for that form. Carried for the wire and
   *  for support, never drawn: a parent reading a report has no use for
   *  `statement year-5/measurement/7`. */
  formSource: string | null;
  /** Whether the statements agreed on the form, or it was guessed between ones
   *  that did not. Carried for support and never drawn: the row already says
   *  what it was checked as, and a parent reading "inferred" beside it would
   *  be reading our uncertainty rather than their child's work. */
  formBinding: string | null;
}

/** The heading over a row: the state's own phrase, capitalised.
 *
 *  Nothing else is capitalised or punctuated here, so the heading and the chip
 *  are the same words and a parent can match one to the other. */
export function headingFor(state: ChipState): string {
  return capitalise(QUESTION_STATE[state]);
}

/** What the service means by each status, said to a parent. Wrong answers
 *  only: anything `is_correct` says is right takes the right heading whatever
 *  its status is.
 *
 *  One status keeps a heading of its own. A question nobody attempted is not a
 *  cause we have not settled, it is a blank, and calling a blank "wrong, cause
 *  not settled" is a claim about a child that the page does not support. Every
 *  other shade the service sends is one of the two wrong states, and the
 *  detail a status carries beyond that goes in the row's own body, where the
 *  mechanisms already are. */
function verdictOf(finding: Finding, state: ChipState): string {
  if (finding.status === "not attempted") return "Not attempted";
  return headingFor(state);
}

/** A cross with a named cause is a stronger claim than a dash, so only a
 *  status the service called `identified` earns one. */
function chipOf(finding: Finding, right: boolean): ChipState {
  if (right) return "right";
  return finding.status === "identified" && finding.mechanisms.length > 0
    ? "cause"
    : "no-cause";
}

/** Right or wrong, from the field the service settles it in.
 *
 *  `is_correct` is the answer. Where a service too old to send it left the
 *  field off, `status` is read instead, which is what the field was derived
 *  from before it existed. */
function isRight(finding: Finding): boolean {
  if (typeof finding.is_correct === "boolean") return finding.is_correct;
  return finding.status === "correct";
}

/** What the service wants printed over a question it could not mark. Its own
 *  words where it sent them: the wording is a claim about what happened to a
 *  question, and a client phrase reading "Could not read" over a question that
 *  was never in the photograph blames the picture for a fact about the page. */
/** What each answer form is called in a sentence a parent reads.
 *
 *  The service's names are for the service: `measure_with_unit` and
 *  `coordinate_or_vector` are keys, not English. Anything not named here falls
 *  back to the key with its underscores opened out, which reads acceptably for
 *  every form in the list today and does not go silent on one added tomorrow.
 */
const FORM_NAME: Record<string, string> = {
  integer: "a whole number",
  decimal: "a decimal",
  fraction: "a fraction",
  percentage: "a percentage",
  ratio: "a ratio",
  standard_form: "standard form",
  exact_surd_pi: "an exact value",
  angle_degrees: "an angle in degrees",
  measure_with_unit: "a measurement with its unit",
  probability: "a probability",
  statistic: "a statistic",
  sequence_terms: "a sequence of terms",
  set_or_list: "a list of values",
  ordered_list: "a list in order",
  coordinate_or_vector: "a coordinate",
  interval_or_inequality: "an inequality",
  equation_solution: "a solution to an equation",
  expression: "an algebraic expression",
  written_calculation: "a written calculation",
  graph_or_drawing: "a drawing",
  proof_or_reasoning: "a piece of reasoning",
  description: "a description",
};

/** The one line saying what this answer was checked as, or null.
 *
 *  Null where the service named no form, and null where the evidence already
 *  says it: the service writes "Checked as a decimal to 2 places: 8.4 is not
 *  8.38" onto the row it applies to, and that sentence carries the precision
 *  as well as the form. A second line above it saying "Checked as a decimal"
 *  would be the same fact twice, with the useful half missing.
 */
export function formLine(row: Row): string | null {
  if (!row.form) return null;
  /* Never over a row nobody marked. "Checked as a drawing" is not true of a
     question that stopped because a drawing cannot be checked, and the verdict
     over it already says what happened. */
  if (!row.read) return null;
  const name = FORM_NAME[row.form] ?? row.form.replace(/_/g, " ");
  if (row.form === "description") {
    return `Answer type: ${name}. No deterministic comparison.`;
  }
  if (row.formCheck === "unparsed") return `Could not compare this answer as ${name}.`;
  if (row.formCheck !== "agree" && row.formCheck !== "differ") return null;
  if (row.formCheck === "agree" && !row.right) {
    return "The form comparison agrees, but does not establish that this answer is correct.";
  }
  if (row.formCheck === "differ" && row.right) {
    return "The form comparison differs; the original marking verdict is retained.";
  }
  if (row.evidence.some((line) => /^checked as /i.test(line.trim()))) return null;
  const accuracy = row.formAccuracy;
  const precision = accuracy && Number.isInteger(accuracy.n)
    ? ` to ${accuracy.n} ${accuracy.kind === "dp" ? "place" : "significant figure"}${accuracy.n === 1 ? "" : "s"}`
    : "";
  const simplest = row.formRequirements?.simplest ? " in simplest form" : "";
  return `Checked as ${name}${precision}${simplest}.`;
}

function unmarkableVerdict(row: Unmarkable): string {
  if (row.verdict) return row.verdict;
  switch (row.reason) {
    case "drawn": return "A drawing, so there is nothing for us to read";
    case "not photographed": return "Not in the photograph";
    /* Two reasons that replace "no mechanism library", which named the one
       thing they have in common and none of what separates them. A form with
       no deterministic comparison needs a person; a form with one and no right
       answer to compare against is our backlog. The service sends its own
       `verdict` for both and this is the fallback for a body stored without
       one. `startsWith`, because each carries the form on the end of it. */
    /* V3. An answer that IS on the paper and could not be made out, which the
       read used to fold into `blank` and so into "not attempted". That is a
       claim about a child made out of a limit of ours, and it is the one the
       verdict below refuses to make. */
    case "unreadable": return "Could not read the answer";
    default:
      if (row.reason?.startsWith("not machine-checkable")) {
        return "Needs a person to check";
      }
      if (row.reason?.startsWith("no answer source")) return "Not marked";
      return headingFor("unread");
  }
}

/** Question labels the way they run down a paper: A before B, Q2 before Q10.
 *
 *  The service groups findings by topic, and topic order is not page order, so
 *  a grid built in wire order runs K to U and then back to A. This grid is
 *  called "the page at a glance" and is meant to be read against the paper in
 *  front of the parent, so it is sorted the way the paper is numbered. The
 *  list of rows below it is ordered by something else entirely and on purpose:
 *  wrong answers first. */
const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

function byLabel(a: Row, b: Row): number {
  return collator.compare(a.label, b.label);
}

function title(topic: Topic): string | null {
  return topic.lesson?.title ?? null;
}

/** Every question on the page, marked and unmarkable alike, in paper order. */
export function rowsOf(report: Report): Row[] {
  const rows: Row[] = [];
  for (const [t, topic] of (report.topics ?? []).entries()) {
    for (const [f, finding] of (topic.findings ?? []).entries()) {
      const right = isRight(finding);
      const chip = chipOf(finding, right);
      rows.push({
        key: `f-${t}-${f}-${finding.question}`,
        label: finding.question,
        chip,
        right,
        read: true,
        verdict: right ? headingFor("right") : verdictOf(finding, chip),
        wrote: finding.answer_written ?? null,
        answer: finding.expected === null || finding.expected === undefined
          ? null : String(finding.expected),
        mechanisms: finding.mechanisms ?? [],
        evidence: finding.evidence ?? [],
        topic: title(topic),
        /* Never on a model row, whatever arrived. The service sends no
           `worked` with one, and a screen that offered "How to get there" from
           a route nobody has checked would be doing the one thing the label on
           the row exists to stop. */
        worked: finding.verified === false || finding.source === "corpus" ? null : finding.worked ?? null,
        /* A derivation is our own deterministic arithmetic, not the reader's
           opinion, so it never wears the reader label and never reads as
           provisional. Stated rather than left to follow from the absence of
           `verified`, because that absence is what a future field would take
           away without anyone noticing. */
        modelMarked: finding.source !== "derivation" && finding.verified === false,
        corpusMarked: finding.source === "corpus",
        provisional: finding.source !== "derivation"
          && (finding.marking_trust === "provisional" || finding.verified === false
          || finding.source === "model"
          || (finding.source === "corpus" && finding.verified !== "tutor")),
        corpusVerified: finding.source === "corpus" && finding.verified === "tutor"
          && finding.marking_trust !== "provisional",
        verificationNotice: finding.verification_notice,
        form: finding.form ?? null,
        formCheck: finding.form_check ?? null,
        formAccuracy: finding.form_accuracy ?? null,
        formRequirements: finding.form_requirements ?? null,
        formSource: finding.form_source ?? null,
        formBinding: finding.form_binding ?? null,
      });
    }
    for (const [u, row] of (topic.unmarkable ?? []).entries()) {
      rows.push({
        key: `u-${t}-${u}-${row.label}`,
        label: row.label,
        chip: "unread",
        right: false,
        read: false,
        verdict: unmarkableVerdict(row),
        wrote: null,
        answer: null,
        mechanisms: [],
        evidence: [],
        topic: title(topic),
        /* An unmarkable row has no expected answer, so there is no route to
           write and nothing to fold open. */
        worked: null,
        /* Nobody marked it, so nobody marked it with the reader either, and
           nothing about it came out of the corpus. */
        modelMarked: false,
        corpusMarked: false,
        /* The form is on an unmarked row too, where the service could name
           one, because there it is the reason rather than a check. Nothing was
           compared, so `formCheck` stays null. */
        form: row.form ?? null,
        formCheck: row.form_check ?? null,
        formSource: row.form_source ?? null,
        formBinding: row.form_binding ?? null,
      });
    }
  }
  return rows.sort(byLabel);
}

/** The questions that went wrong, and then everything else. The order the
 *  screen reads them out in: a parent opening a report is looking for what
 *  happened, and the seventeen that were right are folded away below. */
export function wrongFirst(rows: Row[]): Row[] {
  const wrong = rows.filter((r) => r.read && !r.right);
  const unread = rows.filter((r) => !r.read);
  return [...wrong, ...unread];
}

export function rightRows(rows: Row[]): Row[] {
  return rows.filter((r) => r.right);
}

/** What, if anything, this page keeps doing.
 *
 * Three cases, and they are not the same claim, so the screen writes a
 * different sentence for each rather than one sentence with a number in it.
 *
 *   repeat   one cause is behind two or more WRONG answers. The strongest
 *            thing this product can say, and what the section is named for.
 *   several  two or more wrong answers and no cause shared between them.
 *   single   one wrong answer, with or without a cause named for it.
 *
 * **Only wrong answers are counted, and that is a correction rather than a
 * simplification.** Counting mechanisms across every finding made the recorded
 * tier A page lead with "left the number unchanged: the same step on eight
 * answers". True, and useless, and close to faulting work that was right:
 * seven of those eight answers were correct, and the service attaches a
 * mechanism to a correct answer to say how the method may have got there
 * rather than to mark it. A section at the top of a report is about what went
 * wrong, or it is not on the screen.
 */
export type PatternKind = "repeat" | "several" | "single";

export interface Pattern {
  kind: PatternKind;
  /** The cause, where one covers the questions named. Null on `several`, and
   *  on a `single` the service would not explain. */
  mechanism: string | null;
  /** The wrong questions this is about, in paper order. */
  questions: string[];
  /** How many answers went wrong on the page altogether. */
  wrongCount: number;
  /** How many of those carry any named cause at all. Zero is a different
   *  sentence from "not the same reason", which implies there were reasons. */
  named: number;
}

function tally(rows: Row[]): Map<string, string[]> {
  const seen = new Map<string, string[]>();
  for (const row of rows) {
    for (const mechanism of row.mechanisms) {
      const held = seen.get(mechanism);
      if (held) held.push(row.label);
      else seen.set(mechanism, [row.label]);
    }
  }
  return seen;
}

function commonest(seen: Map<string, string[]>): [string, string[]] | null {
  let best: [string, string[]] | null = null;
  for (const entry of seen) {
    if (!best || entry[1].length > best[1].length) best = entry;
  }
  return best;
}

export function pattern(rows: Row[]): Pattern | null {
  const wrong = rows.filter((r) => r.read && !r.right && !r.provisional && !r.modelMarked);
  if (wrong.length === 0) return null;

  const named = wrong.filter((r) => r.mechanisms.length > 0).length;
  const shared = commonest(tally(wrong));
  if (shared && shared[1].length >= 2) {
    return {
      kind: "repeat",
      mechanism: shared[0],
      questions: shared[1],
      wrongCount: wrong.length,
      named,
    };
  }

  if (wrong.length === 1) {
    const only = wrong[0]!;
    return {
      kind: "single",
      mechanism: only.mechanisms.length === 1 ? only.mechanisms[0]! : null,
      questions: [only.label],
      wrongCount: 1,
      named,
    };
  }

  return {
    kind: "several",
    mechanism: null,
    questions: wrong.map((r) => r.label),
    wrongCount: wrong.length,
    named,
  };
}

/* ------------------------------------------------------------------ topics */

/** The topic this page is really about: the one the wrong answers are in,
 *  falling back to the first the service resolved to a lesson. A page can span
 *  nine lessons and the practice for it has to be on one of them. */
export function leadTopic(report: Report): Topic | null {
  const topics = (report.topics ?? []).filter((t) => t.status !== "none");
  if (topics.length === 0) return null;
  let best: Topic | null = null;
  let most = 0;
  for (const topic of topics) {
    const wrong = (topic.findings ?? []).filter((f) => !isRight(f)).length;
    if (wrong > most) {
      most = wrong;
      best = topic;
    }
  }
  return best ?? topics.find((t) => t.lesson) ?? topics[0] ?? null;
}

/** What this page is called, in the words a parent would use. The lesson title
 *  before the report's own, because "Rounding decimals to significant figures"
 *  says more than "Crack the Code: Rounding". */
export function pageTitle(report: Report): string {
  return leadTopic(report)?.lesson?.title ?? report.title ?? "This page";
}

/** Practice items, from the lead topic first and topped up from the rest.
 *  Deduplicated on the destination, because a page spanning three lessons on
 *  one unit sends the same Corbettmaths exercise three times. */
export function practiceLinks(report: Report, cap = 5): Link[] {
  const lead = leadTopic(report);
  const ordered = [
    ...(lead?.links ?? []),
    ...(report.topics ?? []).flatMap((t) => (t === lead ? [] : t.links ?? [])),
  ];
  const seen = new Set<string>();
  const kept: Link[] = [];
  for (const link of ordered) {
    if (!link?.url || seen.has(link.url)) continue;
    seen.add(link.url);
    kept.push(link);
    if (kept.length >= cap) break;
  }
  return kept;
}

/** Material we hold the right to reproduce rather than merely link to. The
 *  split between `printable` and `links` is that licence rule expressed as two
 *  fields, so nothing here promotes one into the other. */
export function printables(report: Report, cap = 2): Printable[] {
  const lead = leadTopic(report);
  const ordered = [
    ...(lead?.printable ?? []),
    ...(report.topics ?? []).flatMap((t) => (t === lead ? [] : t.printable ?? [])),
  ];
  const seen = new Set<string>();
  const kept: Printable[] = [];
  for (const item of ordered) {
    const key = item.url ?? item.image ?? item.label ?? "";
    if (!key || seen.has(key)) continue;
    seen.add(key);
    kept.push(item);
    if (kept.length >= cap) break;
  }
  return kept;
}

/* ------------------------------------------------------------------- words */

const WORDS = [
  "no", "one", "two", "three", "four", "five", "six", "seven", "eight",
  "nine", "ten",
];

/** A small count in words, the way the sentence around it is written. Past ten
 *  the digits read better than the word does. */
export function word(n: number): string {
  return n >= 0 && n < WORDS.length ? WORDS[n]! : String(n);
}

/** "P", "P and Q", "P, Q and U". */
export function listOf(items: string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0]!;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]!}`;
}

/** A word with its first letter up, and nothing else touched. `sentence()`
 *  would also add a full stop, which is right for a phrase standing alone and
 *  wrong for a count that opens one: it once rendered "Three. answers came out
 *  wrong". */
export function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A service phrase as a sentence: capitalised, and full-stopped once. The
 *  mechanisms arrive as fragments ("rounded to a whole number instead"),
 *  because they are written to be dropped into a sentence. */
export function sentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return "";
  const capped = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(capped) ? capped : `${capped}.`;
}

/** A question and the cause behind it, joined so the join works for every
 *  mechanism there is.
 *
 *  **A mechanism is a fragment and usually a verb phrase in the past tense.**
 *  "forgot the first term", "doubled the step", "used 360 minus the angle".
 *  Three screens put a copula in front of one and the result was not English:
 *  the walk read "Question Q1 was forgot the first term." off the result screen
 *  and "Because three answers on this page were right shape and turn, drawn 1
 *  square too high." off the practice screen. FP1-06.
 *
 *  A colon is the join because it is the only one that works for the whole
 *  vocabulary. A connective that reads well in front of "forgot the first
 *  term" reads badly in front of "answer ten times too big", and choosing per
 *  mechanism would be this file writing grammar for strings the service owns.
 *  The unit gate asserts the invariant over every mechanism in the recording:
 *  a mechanism never follows "was" or "were", and always follows a colon. */
export function causeLine(label: string, mechanism: string): string {
  return `Question ${label}: ${mechanism}.`;
}

/** The one sentence that says what happened on this page, for the section at
 *  the top. Null where nothing went wrong. */
export function patternSentence(found: Pattern | null): string | null {
  if (!found) return null;
  switch (found.kind) {
    case "repeat":
      return `${capitalise(word(found.questions.length))} answers had the same `
        + `cause: ${found.mechanism}. They were questions `
        + `${listOf(found.questions)}.`;
    case "single":
      return found.mechanism
        ? causeLine(found.questions[0]!, found.mechanism)
        : `Question ${found.questions[0]!} is wrong, and we could not work out `
          + "what caused it. It is below with what was written.";
    case "several":
      return found.named === 0
        ? `${capitalise(word(found.wrongCount))} answers came out wrong, and `
          + "we could not name what caused any of them. Questions "
          + `${listOf(found.questions)} are below with what was written.`
        : `${capitalise(word(found.wrongCount))} answers came out wrong, and `
          + `not for the same reason. Questions ${listOf(found.questions)} are `
          + "below with what was written.";
  }
}

/** The heading over that sentence. It changes with the claim, because a
 *  section headed "the mistake that repeats" over a page where nothing repeated
 *  is a sentence the data does not support. */
export function patternHeading(found: Pattern): string {
  return found.kind === "repeat" ? "The mistake that repeats" : "What went wrong";
}

/** "question" or "questions", for a sentence that counts them. */
export function pageWord(n: number): string {
  return n === 1 ? "question" : "questions";
}

/** The collapsed heading over the answers that were right.
 *
 *  Here rather than inline in the JSX because the verb has to follow the
 *  count and the count is the service's: a page with one right answer read
 *  "The one that were right" on the integrator's walk of 5 September 2026.
 *  One place, one test, and every screen that ever shows this fold agrees. */
export function rightFold(n: number, counted: string): string {
  return `The ${counted} that ${n === 1 ? "was" : "were"} right`;
}

/* --------------------------------------------------- Agent FIX-A2, final ---
 *
 * Who may correct a reading.
 */

/** Whether the learner reading this report may type in what we could not read.
 *
 *  A report has many owners and one canonical one. Every owner reads the page;
 *  only the learner the page was read FOR can correct the reading of it, and
 *  the service refuses the rest. Where the service says so on the report, the
 *  control is never drawn and the refusal is never reached; where it does not,
 *  this answers true and `sayConfirmRefused` catches the refusal instead.
 *
 *  **The field is `can_confirm` and it rides the envelope, not the report.**
 *  The wire ruling of 5 September 2026 settled the name: the service answers
 *  it on `GET /v1/reports/{sha}` and on the done poll body, computed from
 *  `reports.child_id` against the learner header. It is a fact about who is
 *  asking, and the report is the same body for every owner of the page, so it
 *  cannot live inside the report.
 *
 *  **A missing field means yes.** Every report written before the field
 *  existed has one owner as far as this screen can tell, and hiding the
 *  control on a guess would take a correction away from the parent it belongs
 *  to. A boolean is the whole contract: an owner string whose values this file
 *  invented would fail silently in whichever direction the guess went. On the
 *  other side of a wrong guess the service still refuses, with the 403 and the
 *  sentence `sayConfirmRefused` renders. FP1-05.
 */
export function confirmable(canConfirm: boolean | null | undefined): boolean {
  return typeof canConfirm === "boolean" ? canConfirm : true;
}
